import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Compass, Plus, Trash2, Upload } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const empty = { title: "", text: "", category: "À voir", distance: "", link: "", image: "" };

export function RegionSettings() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then(({ data }) => setItems(data.region || [])); }, []);

  const save = async (next) => {
    setBusy(true);
    try { const { data } = await api.put("/settings", { region: next }); setItems(data.region); toast.success("Page région mise à jour"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: BACKEND + data.url }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!items) return null;
  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <div className="rounded-2xl bg-white border border-border p-5" data-testid="region-settings">
      <h2 className="font-serif text-2xl text-olive flex items-center gap-2"><Compass className="w-5 h-5 text-rose-brand" /> À faire dans la région</h2>
      <p className="text-sm text-muted-foreground mt-1">Vos bonnes adresses affichées sur la page « La région » (balades, visites, restaurants…).</p>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-4 items-end">
        <div className="md:col-span-2"><Label className="text-xs">Titre</Label><Input {...f("title")} placeholder="Ex : Parc du Haut-Fourneau U4" data-testid="region-title" /></div>
        <div><Label className="text-xs">Catégorie</Label><Input {...f("category")} placeholder="À voir / Balades / Restaurants" data-testid="region-category" /></div>
        <div><Label className="text-xs">Distance</Label><Input {...f("distance")} placeholder="10 min en voiture" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Lien (site, Google Maps…)</Label><Input {...f("link")} placeholder="https://…" /></div>
        <div className="md:col-span-5"><Label className="text-xs">Description</Label><Textarea rows={2} {...f("text")} data-testid="region-text" /></div>
        <label className="h-10 flex items-center justify-center gap-1 rounded-md border border-dashed border-olive text-olive text-xs cursor-pointer">
          <Upload className="w-3.5 h-3.5" /> {form.image ? "Photo ✓" : "Photo"}
          <input type="file" accept="image/*" onChange={upload} className="hidden" />
        </label>
      </div>
      <Button onClick={() => { save([...items, form]); setForm(empty); }} disabled={busy || !form.title} className="mt-3 bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="region-add"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      <div className="mt-4 divide-y divide-border" data-testid="region-list">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            {it.image ? <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-secondary" />}
            <div className="flex-1 min-w-0"><p className="font-semibold text-olive truncate">{it.title} <span className="text-xs text-muted-foreground font-normal">· {it.category}{it.distance ? ` · ${it.distance}` : ""}</span></p><p className="text-xs text-muted-foreground truncate">{it.text}</p></div>
            <button onClick={() => save(items.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`region-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground py-3">Aucune suggestion pour l'instant.</p>}
      </div>
    </div>
  );
}
